import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVault } from "../../contexts/VaultContext";
import PageLayout from "../../ui/PageLayout.jsx";
import PageHeader from "../../ui/PageHeader.jsx";
import WidgetShell from "../../ui/WidgetShell.jsx";
import Button from "../../ui/Button.jsx";
import { JournalBody, SmallText } from "../../ui/Typography.jsx";

/**
 * Journal Page (Vault-backed)
 * - Full-page editor + searchable list
 * - Tagging, pinning, delete, autosave
 * - Handwriting font (Caveat) loaded dynamically
 * - Emits mudo:journal-updated + mudo:journal-entry-created
 */

function loadFontOnce() {
  if (document.getElementById("mudo-font-caveat")) return;
  const link = document.createElement("link");
  link.id = "mudo-font-caveat";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap";
  document.head.appendChild(link);
}
const todayKey = (d = new Date()) => d.toISOString().slice(0, 10);

export default function Journal({ isDark = false }) {
  const navigate = useNavigate();
  const { ready, getJSON, setJSON, saveFile, getFileUrl, deleteFile, hasFolder, isDriveAuthed } = useVault();

  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");
  const [q, setQ] = useState("");
  const [pinnedOnly, setPinnedOnly] = useState(false);

  const [draftImages, setDraftImages] = useState([]); // [{ id, file, previewUrl }]
  const [savingImages, setSavingImages] = useState(false);
  const [viewer, setViewer] = useState(null); // { relPath }

  const fileInputRef = useRef(null);
  const perEntryInputRef = useRef(null);
  const [perEntryTargetId, setPerEntryTargetId] = useState(null);

  useEffect(() => loadFontOnce(), []);

  // Cleanup draft image previews
  useEffect(() => {
    return () => {
      try {
        for (const it of draftImages) {
          if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
        }
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canStoreFiles = !!saveFile && (hasFolder || isDriveAuthed);

  const guessExtension = (mime) => {
    const m = String(mime || "").toLowerCase();
    if (m.includes("webp")) return "webp";
    if (m.includes("png")) return "png";
    if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
    if (m.includes("gif")) return "gif";
    return "bin";
  };

  const getNextImageIndex = (attachments = []) => {
    let max = 0;
    for (const a of Array.isArray(attachments) ? attachments : []) {
      const p = String(a?.relPath || "");
      const m = p.match(/image-(\d+)\./i);
      if (m) max = Math.max(max, Number(m[1]) || 0);
    }
    return max + 1;
  };

  const fileToWebp = async (file, { maxEdge = 1600, quality = 0.86 } = {}) => {
    try {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      const outW = Math.max(1, Math.round(width * scale));
      const outH = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no_canvas_ctx");
      ctx.drawImage(bitmap, 0, 0, outW, outH);
      bitmap.close?.();

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("toBlob_failed"))),
          "image/webp",
          quality
        );
      });
      return /** @type {Blob} */ (blob);
    } catch {
      return null;
    }
  };

  const storeImagesForEntry = async (entryId, files, existingAttachments = []) => {
    const createdAt = new Date().toISOString();
    let nextIndex = getNextImageIndex(existingAttachments);
    const added = [];

    for (const file of Array.from(files || [])) {
      if (!file || !String(file.type || "").startsWith("image/")) continue;

      const webp = await fileToWebp(file);
      const blob = webp || file;
      const mimeType = String(blob.type || file.type || "application/octet-stream");
      const ext = webp ? "webp" : guessExtension(mimeType);
      const fileName = `image-${nextIndex}.${ext}`;
      const relPath = `Journal/${entryId}/${fileName}`;

      const ok = await saveFile(relPath, blob, { mimeType });
      if (!ok) continue;

      added.push({
        id: crypto.randomUUID?.() || `att_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        relPath,
        name: fileName,
        type: mimeType,
        size: Number(blob.size || 0),
        originalName: file.name || "",
        createdAt,
      });
      nextIndex += 1;
    }

    return added;
  };

  // One-time migration from localStorage → vault (if any)
  useEffect(() => {
    if (!ready) return;
    (async () => {
      const vaultExisting = await getJSON("journal", null);
      if (vaultExisting) { setItems(Array.isArray(vaultExisting) ? vaultExisting : []); return; }

      try {
        const raw = localStorage.getItem("mudoJournal");
        if (raw) {
          const parsed = JSON.parse(raw);
          const safe = Array.isArray(parsed) ? parsed : [];
          await setJSON("journal", safe);
          localStorage.removeItem("mudoJournal");
          setItems(safe);
          window.dispatchEvent(new Event("mudo:journal-updated"));
          return;
        }
      } catch {}

      await setJSON("journal", []);
      setItems([]);
    })();
  }, [ready, getJSON, setJSON]);

  // Refresh after Receive
  useEffect(() => {
    if (!ready) return;
    const onRestore = async () => {
      const fresh = await getJSON("journal", []);
      setItems(Array.isArray(fresh) ? fresh : []);
    };
    window.addEventListener("mudo:vault-restored", onRestore);
    return () => window.removeEventListener("mudo:vault-restored", onRestore);
  }, [ready, getJSON]);

  const save = (next) => {
    setItems(next);
    setJSON("journal", next);
    window.dispatchEvent(new Event("mudo:journal-updated"));
  };

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    if (draftImages.length && !canStoreFiles) {
      alert("To attach images, connect a workspace folder or Drive.");
      return;
    }

    const entry = {
      id: crypto.randomUUID?.() || `jr_${Date.now()}`,
      date: todayKey(),
      createdAt: new Date().toISOString(),
      text: t,
      tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
      pinned: false,
      attachments: [],
    };

    try {
      if (draftImages.length) {
        setSavingImages(true);
        const added = await storeImagesForEntry(entry.id, draftImages.map((d) => d.file), []);
        entry.attachments = added;
      }
    } finally {
      setSavingImages(false);
    }

    const next = [entry, ...items];
    save(next);
    // Notify streak system immediately
    window.dispatchEvent(new CustomEvent("mudo:journal-entry-created", { detail: { id: entry.id, date: entry.date } }));
    setText("");
    setTags("");

    // Clear draft images
    try {
      for (const it of draftImages) {
        if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      }
    } catch {}
    setDraftImages([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDraftPickImages = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const next = files
      .filter((f) => f && String(f.type || "").startsWith("image/"))
      .map((file) => ({
        id: crypto.randomUUID?.() || `dimg_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
    setDraftImages((prev) => [...prev, ...next]);
  };

  const removeDraftImage = (id) => {
    setDraftImages((prev) => {
      const hit = prev.find((p) => p.id === id);
      try {
        if (hit?.previewUrl) URL.revokeObjectURL(hit.previewUrl);
      } catch {}
      return prev.filter((p) => p.id !== id);
    });
  };

  const addImagesToExistingEntry = async (entryId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!canStoreFiles) {
      alert("To attach images, connect a workspace folder or Drive.");
      return;
    }

    const cur = items.find((i) => i.id === entryId);
    if (!cur) return;

    setSavingImages(true);
    try {
      const existing = Array.isArray(cur.attachments) ? cur.attachments : [];
      const added = await storeImagesForEntry(entryId, files, existing);
      if (!added.length) return;
      const next = items.map((i) => (i.id === entryId ? { ...i, attachments: [...existing, ...added] } : i));
      save(next);
    } finally {
      setSavingImages(false);
      if (perEntryInputRef.current) perEntryInputRef.current.value = "";
      setPerEntryTargetId(null);
    }
  };

  const removeAttachment = async (entryId, attId) => {
    const cur = items.find((i) => i.id === entryId);
    if (!cur) return;
    const existing = Array.isArray(cur.attachments) ? cur.attachments : [];
    const att = existing.find((a) => a.id === attId);
    if (!att) return;

    try {
      await deleteFile(att.relPath);
    } catch {}

    const nextAtt = existing.filter((a) => a.id !== attId);
    const next = items.map((i) => (i.id === entryId ? { ...i, attachments: nextAtt } : i));
    save(next);
  };

  const AttachmentThumb = ({ relPath, onClick }) => {
    const [url, setUrl] = useState(null);

    useEffect(() => {
      let alive = true;
      (async () => {
        const u = await getFileUrl(relPath);
        if (alive) setUrl(u);
      })();
      return () => {
        alive = false;
      };
    }, [relPath]);

    return (
      <button
        type="button"
        onClick={onClick}
        className="relative w-20 h-20 rounded overflow-hidden border border-black/10 bg-black/10"
        title="Open image"
      >
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs opacity-70">Loading…</div>
        )}
      </button>
    );
  };

  const remove = (id) => {
    const next = items.filter((i) => i.id !== id);
    save(next);
  };

  const togglePin = (id) => {
    const next = items.map((i) => (i.id === id ? { ...i, pinned: !i.pinned } : i));
    save(next);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const arr = items.filter((i) => {
      const hay = `${i.text || ""} ${(i.tags || []).join(" ")} ${i.date || ""}`;
      const hit = !needle || hay.toLowerCase().includes(needle);
      const pinOk = !pinnedOnly || i.pinned;
      return hit && pinOk;
    });
    return arr.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    });
  }, [items, q, pinnedOnly]);

  const glass = {
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.70)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: isDark ? "1px solid rgba(255,255,255,.12)" : "1px solid rgba(0,0,0,.08)",
    borderRadius: "12px",
    boxShadow: "0 8px 22px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.06)",
  };
  const card = {
    background: isDark ? "rgba(0,0,0,.30)" : "rgba(255,255,255,.95)",
    border: isDark ? "1px solid rgba(255,255,255,.10)" : "1px solid rgba(0,0,0,.06)",
    borderRadius: "14px",
  };

  return (
    <div 
      className={`p-4 md:p-6 ${isDark ? "text-white" : "text-neutral-900"}`}
      style={{ backgroundColor: 'var(--color-app-bg)' }}
    >
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="px-3 py-1.5 rounded text-white text-sm transition-colors"
              style={{ backgroundColor: 'var(--color-primary)' }}
              title="Back to dashboard"
            >
              ← Dashboard
            </button>
            <h1 className="text-2xl font-bold">Journal</h1>
          </div>

          <div className="flex items-center gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="px-3 py-1.5 rounded text-sm"
              style={{
                background: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
                border: isDark ? "1px solid rgba(255,255,255,.15)" : "1px solid rgba(0,0,0,.15)",
              }}
            />
            <label className="text-sm flex items-center gap-1 opacity-80">
              <input type="checkbox" checked={pinnedOnly} onChange={(e) => setPinnedOnly(e.target.checked)} />
              Pinned only
            </label>
          </div>
        </div>

        {/* Editor */}
        <div className="p-3" style={glass}>
          <div className="grid md:grid-cols-9 gap-3">
            <div className="md:col-span-7">
              <div className="rounded-lg p-3" style={card}>
                <textarea
                  rows={6}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write your thoughts here…"
                  className="w-full bg-transparent outline-none"
                  style={{ fontFamily: `'Caveat', ui-sans-serif, system-ui`, fontSize: "26px", lineHeight: "1.2" }}
                />

                {/* Draft images */}
                <div className="mt-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs opacity-70">Images</div>
                    <div className="flex items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => onDraftPickImages(e.target.files)}
                      />
                      <button
                        type="button"
                        className="px-2 py-1 rounded text-xs bg-black/60 text-white hover:bg-black/70 disabled:opacity-60"
                        disabled={savingImages}
                        onClick={() => fileInputRef.current?.click()}
                        title={canStoreFiles ? "Attach images" : "Connect workspace to attach images"}
                      >
                        + Add images
                      </button>
                    </div>
                  </div>

                  {draftImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {draftImages.map((d) => (
                        <div key={d.id} className="relative">
                          <img
                            src={d.previewUrl}
                            alt=""
                            className="w-20 h-20 rounded object-cover border border-black/10"
                          />
                          <button
                            type="button"
                            onClick={() => removeDraftImage(d.id)}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white text-xs"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="rounded-lg p-3 h-full flex flex-col gap-2" style={card}>
                <div className="text-xs opacity-70">Tags (comma-separated)</div>
                <input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="e.g. glaze, idea, kiln"
                  className="px-2 py-1 rounded text-sm"
                  style={{
                    background: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
                    border: isDark ? "1px solid rgba(255,255,255,.15)" : "1px solid rgba(0,0,0,.15)",
                  }}
                />
                <div className="flex-1" />
                <button 
                  onClick={add} 
                  className="px-3 py-2 rounded text-white transition-colors disabled:opacity-60" 
                  style={{ backgroundColor: 'var(--color-primary)' }}
                  disabled={!text.trim() || savingImages}
                >
                  {savingImages ? "Saving…" : "Add Entry"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="grid md:grid-cols-2 gap-3">
          {filtered.length === 0 ? (
            <div className="opacity-70 text-sm">No entries found.</div>
          ) : (
            filtered.map((it) => (
              <article key={it.id} className="p-3" style={glass}>
                <div className="flex items-center justify-between">
                  <div className="text-xs opacity-70">
                    {it.date} • {new Date(it.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      ref={perEntryInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => addImagesToExistingEntry(perEntryTargetId, e.target.files)}
                    />
                    <button
                      onClick={() => {
                        setPerEntryTargetId(it.id);
                        perEntryInputRef.current?.click();
                      }}
                      className="px-2 py-1 rounded text-xs bg-black/60 text-white hover:bg-black/70 disabled:opacity-60"
                      disabled={savingImages}
                      title={canStoreFiles ? "Attach images" : "Connect workspace to attach images"}
                    >
                      + Images
                    </button>
                    <button
                      onClick={() => togglePin(it.id)}
                      className={`px-2 py-1 rounded text-xs ${
                        it.pinned ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-black/60 text-white hover:bg-black/70"
                      }`}
                    >
                      {it.pinned ? "Pinned" : "Pin"}
                    </button>
                    <button onClick={() => remove(it.id)} className="px-2 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-500">
                      Delete
                    </button>
                  </div>
                </div>
                {!!(it.tags || []).length && (
                  <div className="text-[11px] mt-1 opacity-70">{(it.tags || []).map((t) => `#${t}`).join(" ")}</div>
                )}
                <div className="mt-2" style={{ fontFamily: `'Caveat', ui-sans-serif, system-ui`, fontSize: "24px", whiteSpace: "pre-wrap" }}>
                  {it.text}
                </div>

                {!!(it.attachments || []).length && (
                  <div className="mt-3">
                    <div className="text-xs opacity-70 mb-2">Attachments</div>
                    <div className="flex flex-wrap gap-2">
                      {(it.attachments || []).map((a) => (
                        <div key={a.id} className="relative">
                          <AttachmentThumb
                            relPath={a.relPath}
                            onClick={() => setViewer({ relPath: a.relPath })}
                          />
                          <button
                            type="button"
                            onClick={() => removeAttachment(it.id, a.id)}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white text-xs"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </div>

      {/* Image viewer */}
      {viewer?.relPath && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setViewer(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="max-w-5xl w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => setViewer(null)}
                className="px-3 py-1.5 rounded bg-white/10 text-white hover:bg-white/15"
              >
                Close
              </button>
            </div>
            <ViewerImage relPath={viewer.relPath} getFileUrl={getFileUrl} />
          </div>
        </div>
      )}
    </div>
  );
}

function ViewerImage({ relPath, getFileUrl }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const u = await getFileUrl(relPath);
      if (alive) setUrl(u);
    })();
    return () => {
      alive = false;
    };
  }, [relPath]);

  if (!url) {
    return <div className="w-full h-[60vh] flex items-center justify-center text-white/80">Loading…</div>;
  }
  return (
    <img
      src={url}
      alt=""
      className="w-full max-h-[80vh] object-contain rounded"
    />
  );
}
